import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Clock, MapPin, LogIn, LogOut, Loader2, CheckCircle2 } from "lucide-react";

function fmtDuration(seconds) {
  if (!seconds && seconds !== 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function getGeo() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Geolocalización no disponible"));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });
}

export default function Fichar() {
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  const load = async () => {
    const [c, h] = await Promise.all([
      api.get("/timeclock/current").then((r) => r.data).catch(() => null),
      api.get("/timeclock/mine", { params: { limit: 30 } }).then((r) => r.data).catch(() => []),
    ]);
    setCurrent(c);
    setHistory(h);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!current) return;
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [current]);

  const elapsed = current
    ? Math.max(0, Math.floor((Date.now() - new Date(current.entry_at).getTime()) / 1000))
    : 0;

  const doClockIn = async () => {
    setLoading(true);
    try {
      const geo = await getGeo();
      await api.post("/timeclock/clock-in", geo);
      toast.success("Jornada iniciada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Error al fichar entrada");
    } finally { setLoading(false); }
  };

  const doClockOut = async () => {
    setLoading(true);
    try {
      const geo = await getGeo();
      await api.post("/timeclock/clock-out", geo);
      toast.success("Jornada cerrada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Error al fichar salida");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="fichar-page">
      <header>
        <div className="label-up text-[#F5A623]">Registro de horario</div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">Fichar</h1>
        <p className="text-zinc-600 text-sm mt-1">
          Marca tu entrada y salida. Guardamos automáticamente la ubicación GPS.
        </p>
      </header>

      {/* Current status */}
      <section className={`rounded-3xl p-6 text-white ${current ? "gradient-sky" : "bg-gradient-to-br from-slate-700 to-slate-800"}`}>
        {current ? (
          <>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-bold text-[#FFC740]">
              <CheckCircle2 size={12} /> Jornada abierta
            </div>
            <div className="font-display font-extrabold text-5xl mt-3 tabular-nums">
              {fmtDuration(elapsed)}
            </div>
            <div className="text-sm text-white/80 mt-1">
              Entrada: {fmtDateTime(current.entry_at)}
            </div>
            <div className="mt-2 text-xs inline-flex items-center gap-1.5 text-white/90">
              <MapPin size={12} /> {current.entry_lat.toFixed(5)}, {current.entry_lng.toFixed(5)}
              {current.entry_accuracy && <span className="text-white/60">· ±{Math.round(current.entry_accuracy)}m</span>}
            </div>
            <button
              onClick={doClockOut}
              disabled={loading}
              data-testid="clock-out-btn"
              className="mt-5 w-full h-14 rounded-2xl bg-white text-[#0B2A4A] font-bold text-lg inline-flex items-center justify-center gap-2 hover:bg-[#FFC740] transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin" /> : <LogOut />}
              Fichar SALIDA
            </button>
          </>
        ) : (
          <>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-bold text-slate-300">
              <Clock size={12} /> Fuera de jornada
            </div>
            <div className="font-display font-extrabold text-3xl mt-3">Empieza tu día</div>
            <p className="text-sm text-white/70 mt-1 max-w-md">
              Se registrará la hora y la ubicación GPS. Necesitamos permiso del navegador.
            </p>
            <button
              onClick={doClockIn}
              disabled={loading}
              data-testid="clock-in-btn"
              className="mt-5 w-full h-14 rounded-2xl bg-gradient-to-r from-[#FF8A1F] to-[#F5A623] text-white font-bold text-lg inline-flex items-center justify-center gap-2 hover:brightness-110 transition-all disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin" /> : <LogIn />}
              Fichar ENTRADA
            </button>
          </>
        )}
      </section>

      {/* History */}
      <section className="brutal-card p-5">
        <h2 className="font-display font-extrabold text-xl">Historial</h2>
        {!history.length ? (
          <p className="text-sm text-slate-500 mt-2">Sin fichajes todavía.</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-200" data-testid="fichar-history">
            {history.map((e) => (
              <li key={e.id} className="py-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500">
                  <Clock size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">
                    {fmtDateTime(e.entry_at)}
                    {e.exit_at && <> → {new Date(e.exit_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</>}
                  </div>
                  <div className="text-[11px] text-slate-500 inline-flex items-center gap-1 mt-0.5">
                    <MapPin size={10} /> {e.entry_lat.toFixed(4)}, {e.entry_lng.toFixed(4)}
                    {e.exit_lat != null && <> · Salida: {e.exit_lat.toFixed(4)}, {e.exit_lng.toFixed(4)}</>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display font-extrabold text-base text-[#0B2A4A]">
                    {e.duration_seconds != null ? fmtDuration(e.duration_seconds) : "…"}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    {e.exit_at ? "cerrada" : "abierta"}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
